import React, { useState } from 'react';

const datasets = [
  { name: "Apple vs Banana", items: [{ label: "Apple", emoji: "🍎" }, { label: "Banana", emoji: "🍌" }] },
  { name: "Cat vs Dog", items: [{ label: "Cat", emoji: "🐱" }, { label: "Dog", emoji: "🐶" }] },
  { name: "Car vs Bike", items: [{ label: "Car", emoji: "🚗" }, { label: "Bike", emoji: "🚲" }] },
  { name: "1 vs 2", items: [{ label: "One", emoji: "1️⃣" }, { label: "Two", emoji: "2️⃣" }] },
  { name: "Happy vs Sad", items: [{ label: "Happy", emoji: "😀" }, { label: "Sad", emoji: "😢" }] },
  { name: "Sun vs Moon", items: [{ label: "Sun", emoji: "☀️" }, { label: "Moon", emoji: "🌙" }] },
  { name: "Book vs Pencil", items: [{ label: "Book", emoji: "📚" }, { label: "Pencil", emoji: "✏️" }] },
  { name: "Fire vs Ice", items: [{ label: "Fire", emoji: "🔥" }, { label: "Ice", emoji: "❄️" }] }
];

const SupervisedLearningGame = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [trainCount, setTrainCount] = useState(0);
  const [trainedLabelMap, setTrainedLabelMap] = useState({});
  const [droppedTrainingEmojis, setDroppedTrainingEmojis] = useState([]);
  const [droppedTestEmoji, setDroppedTestEmoji] = useState(null);
  const [selectedDataset, setSelectedDataset] = useState(datasets[0]);
  const [showTest, setShowTest] = useState(false);
  const [result, setResult] = useState('');

  const handleDrop = (e) => {
    e.preventDefault();
    const label = e.dataTransfer.getData("text");
    setTrainedLabelMap(prev => ({ ...prev, [label]: (prev[label] || 0) + 1 }));
    setDroppedTrainingEmojis(prev => [...prev, label]);
    setTrainCount(prev => prev + 1);
  };

  const handleTrain = () => {
    if (trainCount >= 6) {
      setShowTest(true);
    } else {
      alert("Add more training examples to continue.");
    }
  };

  const handleTestDrop = (e) => {
    e.preventDefault();
    const label = e.dataTransfer.getData("text");
    setDroppedTestEmoji(label);
    const resultText = trainedLabelMap[label] ? `✅ It's a ${label}!` : `❌ I don't know this.`;
    setResult(resultText);
  };

  const nextDataset = () => {
    const newIndex = (currentIndex + 1) % datasets.length;
    setCurrentIndex(newIndex);
    setSelectedDataset(datasets[newIndex]);
    setTrainedLabelMap({});
    setDroppedTrainingEmojis([]);
    setDroppedTestEmoji(null);
    setTrainCount(0);
    setShowTest(false);
    setResult('');
  };

  const getEmojiByLabel = (label) => {
    const item = selectedDataset.items.find(i => i.label === label);
    return item ? item.emoji : '';
  };

  return (
    <div style={{ fontFamily: 'Comic Sans MS, cursive', padding: 30, background: 'linear-gradient(to right, #FFF3E0, #E1F5FE)' }}>
      <h3 style={{ textAlign: 'center', color: '#4A148C' }}>Train Your AI with Supervised Learning</h3>
      <p style={{ textAlign: 'center' }}>Drag images to the robot to help it learn!</p>

      <div style={{ maxWidth: 1000, margin: 'auto' }}>
        <section style={{ background: '#fff', padding: 30, borderRadius: 16, marginBottom: 30 }}>
          <h5 style={{ color: '#6A1B9A' }}>Step 1: Choose a Dataset</h5>
          <div>
            {datasets.map((d, i) => (
              <button
                key={d.name}
                onClick={() => {
                  setCurrentIndex(i);
                  setSelectedDataset(d);
                  setTrainedLabelMap({});
                  setDroppedTrainingEmojis([]);
                  setDroppedTestEmoji(null);
                  setTrainCount(0);
                  setShowTest(false);
                  setResult('');
                }}
                style={{
                  margin: 5,
                  padding: '10px 20px',
                  borderRadius: 12,
                  border: 'none',
                  background: i === currentIndex ? '#FFB300' : '#FFD54F',
                  cursor: 'pointer'
                }}>
                {d.name}
              </button>
            ))}
          </div>
        </section>

        <section style={{ background: '#fff', padding: 30, borderRadius: 16, marginBottom: 30 }}>
          <h5 style={{ color: '#6A1B9A' }}>Step 2: Explore Your Dataset</h5>
          <div style={{ display: 'flex', gap: 20 }}>
            {selectedDataset.items.map(item => (
              <div key={item.label} style={{ flex: 1, background: '#FFF8E1', borderRadius: 12, padding: 20, textAlign: 'center', border: '2px dashed #FFC107' }}>
                <h3>{item.label}</h3>
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text", item.label)}
                    style={{ fontSize: '2.5rem', cursor: 'grab', margin: 10 }}>
                    {item.emoji}
                  </span>
                ))}
              </div>
            ))}
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            style={{
              marginTop: 30,
              padding: 40,
              background: '#E1F5FE',
              border: '2px dashed #4FC3F7',
              borderRadius: 12,
              textAlign: 'center',
              fontSize: '1.5rem',
              color: '#00796B',
              minHeight: 100
            }}
          >
            Drop your samples here to teach the robot
            <div style={{ marginTop: 10, fontSize: '2rem' }}>
              {droppedTrainingEmojis.map((label, idx) => (
                <span key={idx} style={{ margin: 8 }}>{getEmojiByLabel(label)}</span>
              ))}
            </div>
          </div>

          <button
            onClick={handleTrain}
            style={{ marginTop: 20, background: '#81C784', color: 'white', border: 'none', padding: '12px 25px', fontSize: '1.2rem', borderRadius: 10, cursor: 'pointer' }}>
            🎓 Train Robot
          </button>
        </section>

        {showTest && (
          <section style={{ background: '#fff', padding: 30, borderRadius: 16, marginBottom: 30 }}>
            <h5 style={{ color: '#6A1B9A' }}>Step 3: Test the AI</h5>
            <div style={{ display: 'flex', gap: 20 }}>
              {selectedDataset.items.map(item => (
                <span
                  key={item.label}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text", item.label)}
                  style={{ fontSize: '2.5rem', cursor: 'grab', margin: 10 }}>
                  {item.emoji}
                </span>
              ))}
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleTestDrop}
              style={{
                marginTop: 30,
                padding: 40,
                background: '#F3E5F5',
                border: '2px dashed #CE93D8',
                borderRadius: 12,
                textAlign: 'center',
                fontSize: '1.5rem',
                color: '#6A1B9A',
                minHeight: 100
              }}
            >
              Drop a test image here
              <div style={{ fontSize: '2.5rem', marginTop: 10 }}>
                {droppedTestEmoji && getEmojiByLabel(droppedTestEmoji)}
              </div>
            </div>

            <div style={{ marginTop: 20, fontSize: '1.5rem', fontWeight: 'bold', color: '#4A148C' }}>{result}</div>
          </section>
        )}

        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button
            onClick={nextDataset}
            style={{ backgroundColor: '#4FC3F7', color: 'white', fontSize: '1rem', padding: '10px 20px', borderRadius: 10, border: 'none', margin: '0 10px', cursor: 'pointer' }}>
            🔁 New Dataset
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupervisedLearningGame;
